import type { CrossChainQuote } from '@/lib/api/0x-cross-chain/types';
import {
  ApiBuyTokenUnauthorizedForTradeError,
  ApiInsufficientBalanceError,
  ApiSellAmountTooSmallError,
  ApiSellTokenUnauthorizedForTradeError,
} from '@/lib/errors/api';

import type { Fees, Price, Quote } from '@/utils/0x.types';
import type { TokenResult } from '@/utils/0x/token-registry.types';

import { getAdvancedGrossBuyAmount, type AdvancedOrderEstimate } from './advancedOrderQuote';
import type { TaggedLimitRate } from './limitRateStore';
import type { QuoteErrorInput } from './validation/common';

export type TradeQuoteSource = 'swap' | 'intents' | 'cross-chain' | 'advanced';
export type PriceImpact = { percent: number; usd: number };
export type TokenUsdPrices = {
  sellTokenUsd: number;
  buyTokenUsd: number;
  nativeTokenUsd: number;
};

type TradeQuoteCommon = {
  buyAmount: string | undefined;
  priceImpact: PriceImpact | undefined;
  fee: { totalNetworkFee: string | undefined; fees: Fees | undefined } | undefined;
  isStale: boolean;
  error: Error | undefined;
  quoteError: QuoteErrorInput | undefined;
};

/** A market (standard or intents) quote; `raw` is defined once `status` is `fetched`. */
export type MarketTradeQuote = TradeQuoteCommon & {
  source: 'swap' | 'intents';
  status: 'idle' | 'loading' | 'fetched' | 'error';
  raw: Price | Quote | undefined;
};

/** A cross-chain quote; `raw` is defined once `status` is `fetched`. */
export type CrossChainTradeQuote = TradeQuoteCommon & {
  source: 'cross-chain';
  status: 'idle' | 'loading' | 'fetched' | 'error';
  raw: CrossChainQuote | undefined;
};

/**
 * An Advanced-lane output. `buyAmount` is the gross sell × rate that fixes the
 * order's executable terms; `estimate` is Definitive's net-of-fees view of the
 * same form, present once quoted (never for 1inch).
 */
export type AdvancedTradeQuote = TradeQuoteCommon & {
  source: 'advanced';
  status: 'idle' | 'loading' | 'derived' | 'error';
  raw: undefined;
  estimate: AdvancedOrderEstimate | undefined;
};

export type TradeQuote = MarketTradeQuote | CrossChainTradeQuote | AdvancedTradeQuote;
export type TradeQuoteStatus = TradeQuote['status'];

function classifyQuoteError(error: Error | undefined): QuoteErrorInput | undefined {
  if (!error) return undefined;
  if (error instanceof ApiSellAmountTooSmallError) return { code: 'sell-amount-too-small' };
  if (error instanceof ApiBuyTokenUnauthorizedForTradeError) {
    return { code: 'buy-token-unauthorized' };
  }
  if (error instanceof ApiSellTokenUnauthorizedForTradeError) {
    return { code: 'sell-token-unauthorized' };
  }
  // INSUFFICIENT_BALANCE_OR_ALLOWANCE stays unknown: it is ambiguous between balance and allowance, and allowance is owned by the approval flow.
  if (error instanceof ApiInsufficientBalanceError) return { code: 'insufficient-balance' };
  return { code: 'unknown', error };
}

/** Shared empty fields for the idle/loading/error variants of any lane. */
export function emptyQuoteFields(error?: Error): TradeQuoteCommon & { raw: undefined } {
  return {
    buyAmount: undefined,
    priceImpact: undefined,
    fee: undefined,
    isStale: false,
    raw: undefined,
    error,
    quoteError: classifyQuoteError(error),
  };
}

/**
 * Constructs the gross Advanced-lane output from the pair-tagged rate. This is
 * synchronous; `useAdvancedTradeQuote` layers Definitive's net estimate on top.
 */
export function deriveAdvancedTradeQuote({
  active,
  taggedLimitRate,
  pairKey,
  sellInputDecimal,
  buyToken,
}: {
  active: boolean;
  taggedLimitRate: TaggedLimitRate;
  pairKey: string;
  sellInputDecimal: string;
  buyToken: TokenResult | undefined;
}): AdvancedTradeQuote {
  const idle: AdvancedTradeQuote = {
    ...emptyQuoteFields(),
    source: 'advanced',
    status: 'idle',
    estimate: undefined,
  };
  if (!active || !buyToken) return idle;
  // The form can hold mid-edit placeholders like '.' (see formatLimitRate); Number() rejects them before Decimal parses the string.
  const rateValue = Number(taggedLimitRate.rate.sell);
  const hasMatchingRate =
    taggedLimitRate.pairKey === pairKey && Number.isFinite(rateValue) && rateValue > 0;
  if (!hasMatchingRate) return idle;
  const buyAmount = getAdvancedGrossBuyAmount(
    sellInputDecimal,
    taggedLimitRate.rate.sell,
    buyToken.decimals,
  );
  if (!buyAmount) return idle;
  return { ...idle, status: 'derived', buyAmount };
}
